import { supabase } from "@/integrations/supabase/client";

export type CreatorPaymentProfile = {
  id: string;
  stripe_account_id: string | null;
  stripe_onboarding_complete: boolean | null;
  stripe_charges_enabled: boolean | null;
  stripe_details_submitted: boolean | null;
  stripe_payouts_enabled: boolean | null;
  paypal_payer_id: string | null;
  paypal_connected: boolean | null;
  paypal_email: string | null;
  paypal_country: string | null;
  paypal_country_source: string | null;
  paypal_country_updated_at: string | null;
  manual_payout_country: string | null;
  manual_payout_country_source: string | null;
  manual_payout_country_updated_at: string | null;
  alternate_payout_route_status: string | null;
  alternate_payout_route_updated_at: string | null;
  paypal_country_blocked_payment_count: number | null;
  payout_readiness_status: string | null;
  payout_readiness_code: string | null;
  payout_readiness_message: string | null;
  payout_readiness_updated_at: string | null;
};

export type MarketerPaymentProfile = {
  id: string;
  billing_mode: string | null;
  service_fee: number | null;
  stripe_customer_id: string | null;
  stripe_charges_enabled: boolean | null;
  card_verified_at: string | null;
  paypal_user_id: string | null;
  paypal_charges_enabled: boolean | null;
  paypal_verified_at: string | null;
  paypal_vault_approved_at: string | null;
  primary_payment_method: string | null;
};

export type CampaignCreatorPaymentInfoRow = {
  id: string;
  tiktok_username: string | null;
  stripe_account_id: string | null;
  paypal_payer_id: string | null;
  paypal_email: string | null;
  matched_usernames?: string[] | null;
};

export type CreatorPaymentInfo = {
  id: string;
  stripeAccountId: string | null;
  paypalPayerId: string | null;
  paypalEmail: string | null;
};

export async function fetchCreatorPaymentProfile(
  creatorId?: string | null
): Promise<{ data: CreatorPaymentProfile | null; error: any }> {
  const query = creatorId
    ? (supabase as any).rpc("get_creator_payment_profile", { p_creator_id: creatorId })
    : (supabase as any).rpc("get_my_creator_payment_profile");
  const { data, error } = await query.maybeSingle();
  return { data: (data ?? null) as CreatorPaymentProfile | null, error };
}

export async function fetchMarketerPaymentProfile(
  marketerId?: string | null
): Promise<{ data: MarketerPaymentProfile | null; error: any }> {
  const query = marketerId
    ? (supabase as any).rpc("get_marketer_payment_profile", { p_marketer_id: marketerId })
    : (supabase as any).rpc("get_my_marketer_payment_profile");
  const { data, error } = await query.maybeSingle();
  return { data: (data ?? null) as MarketerPaymentProfile | null, error };
}

export async function fetchCampaignCreatorPaymentInfo({
  campaignId,
  usernames = [],
  creatorIds = [],
  signal,
}: {
  campaignId: string;
  usernames?: string[];
  creatorIds?: string[];
  signal?: AbortSignal;
}): Promise<{ data: CampaignCreatorPaymentInfoRow[]; error: any }> {
  let query = (supabase as any).rpc(
    "get_campaign_creator_payment_info",
    {
      p_campaign_id: campaignId,
      p_usernames: usernames,
      p_creator_ids: creatorIds,
    }
  );
  if (signal) {
    query = query.abortSignal(signal);
  }
  const { data, error } = await query;
  return { data: (data ?? []) as CampaignCreatorPaymentInfoRow[], error };
}

export async function setMyCreatorPaypalEmail(
  email: string
): Promise<{ data: { paypal_email: string } | null; error: any }> {
  const { data, error } = await (supabase as any)
    .rpc("set_my_creator_paypal_email", { p_email: email })
    .maybeSingle();
  return { data: (data ?? null) as { paypal_email: string } | null, error };
}

export async function submitMyAlternatePayoutCountry(
  country: string
): Promise<{
  data: { manual_payout_country: string; alternate_payout_route_status: string } | null;
  error: any;
}> {
  const { data, error } = await (supabase as any)
    .rpc("submit_my_alternate_payout_country", { p_country: country })
    .maybeSingle();

  return {
    data: (data ?? null) as { manual_payout_country: string; alternate_payout_route_status: string } | null,
    error,
  };
}
